import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export type TaskStatus = "Backlog" | "Todo" | "InProgress" | "Blocked" | "Done" | "Cancelled";
export type TaskPriority = "Low" | "Normal" | "High" | "Urgent";

export interface TaskItem {
  id: string;
  title: string;
  description?: string | null;
  status: number;
  priority: number;
  createdByUserId: string;
  assignedToUserId?: string | null;
  tenantId: string;
  dueDate?: string | null;
  completedAt?: string | null;
  completedByUserId?: string | null;
  linkedEntityId?: string | null;
  linkedEntityType?: string | null;
  tags?: string | null;
  sortOrder: number;
  parentTaskId?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface TaskAgenda {
  overdue: TaskItem[];
  today: TaskItem[];
  upcoming: TaskItem[];
  inProgress: TaskItem[];
}

export interface TaskComment {
  id: string;
  taskId: string;
  authorUserId: string;
  contentHtml: string;
  createdAt: string;
}

export function useTasks(status?: number, assignedTo?: string) {
  const params = new URLSearchParams();
  if (status !== undefined) params.set("status", String(status));
  if (assignedTo) params.set("assignedTo", assignedTo);

  return useQuery({
    queryKey: ["tasks", status, assignedTo],
    queryFn: async () => {
      const r = await api().get<TaskItem[]>(`/api/tasks?${params}`);
      return r.data;
    },
  });
}

export function useMyAgenda() {
  return useQuery({
    queryKey: ["tasks", "agenda"],
    queryFn: async () => {
      const r = await api().get<TaskAgenda>("/api/tasks/my-agenda");
      return r.data;
    },
  });
}

export function useSubtasks(parentTaskId: string | null) {
  return useQuery({
    queryKey: ["tasks", parentTaskId, "subtasks"],
    queryFn: async () => {
      const r = await api().get<TaskItem[]>(`/api/tasks/${parentTaskId}/subtasks`);
      return r.data;
    },
    enabled: !!parentTaskId,
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (task: {
      title: string;
      description?: string;
      priority?: number;
      assignedToUserId?: string;
      dueDate?: string;
      parentTaskId?: string;
      linkedEntityId?: string;
      linkedEntityType?: string;
    }) => {
      const r = await api().post<TaskItem>("/api/tasks", task);
      return r.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      if (variables.parentTaskId) {
        qc.invalidateQueries({ queryKey: ["tasks", variables.parentTaskId, "subtasks"] });
      }
    },
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) => {
      const r = await api().put<TaskItem>(`/api/tasks/${id}`, body);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api().post<TaskItem>(`/api/tasks/${id}/complete`);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api().delete(`/api/tasks/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
  });
}

export function useTaskComments(taskId: string) {
  return useQuery({
    queryKey: ["tasks", taskId, "comments"],
    queryFn: async () => {
      const r = await api().get<TaskComment[]>(`/api/tasks/${taskId}/comments`);
      return r.data;
    },
    enabled: !!taskId,
  });
}

export function useCreateComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ taskId, content }: { taskId: string; content: string }) => {
      await api().post(`/api/tasks/${taskId}/comments`, { content });
    },
    onSuccess: (_, { taskId }) => {
      qc.invalidateQueries({ queryKey: ["tasks", taskId, "comments"] });
    },
  });
}
